import rrwebPlayer from "rrweb-player";
import "rrweb-player/dist/style.css";

interface SessionSummary {
  session_id: string;
  shop: string;
  started_at: string;
  duration_ms: number;
  event_count: number;
  entry_url: string | null;
  device: string;
  cart_value: string | null;
  conversion_flag: boolean;
}

interface Manifest {
  session: SessionSummary;
  chunks: string[];
}

const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "") ??
  "http://localhost:8080";

const statusEl = document.getElementById("status")!;
const playerEl = document.getElementById("player")!;
const summaryEl = document.getElementById("summary")!;

function setStatus(msg: string, isError = false): void {
  statusEl.textContent = msg;
  statusEl.className = isError ? "err" : "";
}

async function main(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const shop = params.get("shop");
  const sessionId = params.get("sessionId");
  const token = params.get("token");

  if (!shop || !sessionId || !token) {
    setStatus("Missing shop, sessionId, or token in the URL.", true);
    return;
  }

  setStatus("Fetching session manifest…");
  const manifest = await fetchManifest(shop, sessionId, token);

  renderSummary(manifest.session);

  setStatus(`Downloading ${manifest.chunks.length} chunk(s)…`);
  const events = await loadEvents(manifest.chunks);

  if (events.length < 2) {
    setStatus("Not enough events to replay this session yet.", true);
    return;
  }

  setStatus("");
  // eslint-disable-next-line no-new
  new rrwebPlayer({
    target: playerEl,
    props: {
      events: events as unknown as ConstructorParameters<typeof rrwebPlayer>[0]["props"]["events"],
      autoPlay: true,
      showController: true,
    },
  });
}

async function fetchManifest(
  shop: string,
  sessionId: string,
  token: string
): Promise<Manifest> {
  const url = new URL(`${API_BASE}/v1/replay/manifest`);
  url.searchParams.set("shop", shop);
  url.searchParams.set("sessionId", sessionId);
  url.searchParams.set("token", token);
  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`Manifest request failed (${res.status})`);
  }
  return (await res.json()) as Manifest;
}

// rrweb events are heterogeneous; the player owns the precise type. We keep the
// loader loosely typed and hand the array straight to rrweb-player.
type RrwebEvent = { timestamp: number; [k: string]: unknown };

async function loadEvents(chunkUrls: string[]): Promise<RrwebEvent[]> {
  const all: RrwebEvent[] = [];
  for (const url of chunkUrls) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Chunk fetch failed (${res.status})`);
    const text = await gunzipIfNeeded(await res.arrayBuffer());
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (trimmed) all.push(JSON.parse(trimmed) as RrwebEvent);
    }
  }
  // Events are written in order, but sort defensively by timestamp.
  all.sort((a, b) => a.timestamp - b.timestamp);
  return all;
}

/** Gunzip if the body is still gzip-encoded; otherwise decode as UTF-8 text. */
async function gunzipIfNeeded(buf: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buf);
  const isGzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (!isGzip) return new TextDecoder().decode(bytes);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Response(stream).text();
}

function renderSummary(s: SessionSummary): void {
  const bits = [
    new Date(s.started_at).toLocaleString(),
    `${Math.round(s.duration_ms / 1000)}s`,
    `${s.event_count} events`,
    s.device,
  ];
  if (s.cart_value) bits.push(`cart ${s.cart_value}`);
  if (s.conversion_flag) bits.push("✓ converted");
  summaryEl.textContent = bits.join("  ·  ");
}

main().catch((err) => {
  console.error(err);
  setStatus(err instanceof Error ? err.message : "Failed to load replay.", true);
});
